import { useState } from 'react'
import { Search, Settings, Sparkles } from 'lucide-react'
import { SearchOptions } from '../types'

interface DomainSearchProps {
  onSearch: (keyword: string, options: SearchOptions) => void
  isLoading: boolean
}

const DEFAULT_TLDS = ['com', 'net', 'org', 'io', 'co']
const ALL_TLDS = ['com', 'net', 'org', 'io', 'co', 'app', 'dev', 'me', 'tv', 'cc', 'ly', 'ai', 'tech', 'online', 'store', 'blog', 'info', 'biz']

export default function DomainSearch({ onSearch, isLoading }: DomainSearchProps) {
  const [keyword, setKeyword] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [options, setOptions] = useState<SearchOptions>({
    tlds: DEFAULT_TLDS,
    includeHyphen: true,
    includePrefixSuffix: true
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (keyword.trim() && !isLoading) {
      onSearch(keyword.trim(), options)
    }
  }

  const toggleTld = (tld: string) => {
    setOptions(prev => ({
      ...prev,
      tlds: prev.tlds.includes(tld) 
        ? prev.tlds.filter(t => t !== tld)
        : [...prev.tlds, tld]
    }))
  }

  const selectAllTlds = () => {
    setOptions(prev => ({ ...prev, tlds: ALL_TLDS }))
  }

  const selectDefaultTlds = () => {
    setOptions(prev => ({ ...prev, tlds: DEFAULT_TLDS }))
  }

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="card mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Enter your business name or keywords..."
              className="input w-full text-lg"
              disabled={isLoading}
            />
          </div>
          
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="btn-secondary flex items-center space-x-2"
            disabled={isLoading}
          >
            <Settings className="w-4 h-4" />
            <span>Options</span>
          </button>
          
          <button
            type="submit"
            disabled={!keyword.trim() || isLoading}
            className="btn-primary flex items-center space-x-2 px-8"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            <span>{isLoading ? 'Searching...' : 'Search'}</span>
          </button>
        </div>
      </form>

      {/* Advanced Options */}
      {showAdvanced && (
        <div className="card mb-6 animate-slide-up">
          <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-primary-600" />
            <span>Advanced Options</span>
          </h3>
          
          <div className="space-y-6">
            {/* TLD Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">
                  Top-Level Domains (TLDs)
                </label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={selectDefaultTlds}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Popular
                  </button>
                  <button
                    type="button"
                    onClick={selectAllTlds}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Select All
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-3 sm:grid-cols-6 md:grid-cols-9 gap-2">
                {ALL_TLDS.map(tld => (
                  <label key={tld} className="flex items-center space-x-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={options.tlds.includes(tld)}
                      onChange={() => toggleTld(tld)}
                      className="text-primary-600 rounded"
                    />
                    <span className="text-sm text-gray-700">.{tld}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Generation Options */}
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.includeHyphen}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeHyphen: e.target.checked }))}
                  className="text-primary-600 rounded"
                />
                <span className="text-sm text-gray-700">Include hyphenated variations</span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.includePrefixSuffix}
                  onChange={(e) => setOptions(prev => ({ ...prev, includePrefixSuffix: e.target.checked }))}
                  className="text-primary-600 rounded"
                />
                <span className="text-sm text-gray-700">Include prefixes and suffixes</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Quick suggestions */}
      {!isLoading && keyword === '' && (
        <div className="text-center text-gray-500">
          <p className="mb-4">Try these examples:</p>
          <div className="flex flex-wrap justify-center gap-2">
            {['techstartup', 'myapp', 'creativestudio', 'bloglife', 'smartsolution'].map(example => (
              <button
                key={example}
                onClick={() => setKeyword(example)}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
